import Alpine from "alpinejs";

const endpointUrl = "https://api.submarineplatform.com/graphql";
const tokenUrl = "/apps/platform/tokens";
const formatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

async function graphqlFetch(query: string, variables: Record<string, any>) {
  const response = await fetch(endpointUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": await fetchToken(),
    },
    body: JSON.stringify({ query, variables }),
  });

  return await response.json();
}

function daysUntilDate(dateString: string): number {
  const today = new Date(); // Current date
  today.setHours(0, 0, 0, 0); // Normalize to midnight to avoid time differences

  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0); // Normalize to midnight

  const diffTime = targetDate.getTime() - today.getTime(); // Difference in milliseconds
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)); // Convert ms to days

  return diffDays;
}

async function fetchToken() {
  try {
    const response = await fetch(tokenUrl, {
      method: "GET",
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error("Error fetching token:", error);
  }
}

export function initSubmarineComponent() {
  Alpine.data("submarineManageSubscription", (subscriptionId: String) => {
    return {
      feedbackModal: {} as { title: string; content: string; link: string; open: boolean },
      getDate(myDate: Date) {
        return formatter.format(new Date(myDate));
      },
      paymentMethodUpdateEmailSent: false,
      loading: true,
      updateAddressLoading: false,
      openAddressUpdateModal: false,
      toggleOpenAddressUpdateModal() {
        this.openAddressUpdateModal = !this.openAddressUpdateModal;
      },
      subscription: {} as {
        order: {
          name: string;
        };
        lines: [
          {
            productVariant: {
              title: string;
            };
          },
        ];
        lastProcessedOrder: Date;
        nextBillingAt: Date;
        paymentMethod: {
          activePaymentInstrument: {
            paymentSource: {
              last4: string;
            };
          };
        };
        deliveryMethod: {
          address: {
            city: string;
            country: {
              name: string;
            };
            firstName: string;
            lastName: string;
            phone: string;
            postcode: string;
            province: {
              code: string;
            };
            street1: string;
            street2: string;
          };
        };
        nextScheduledOrder: {
          paymentIntent: {
            amount: {
              amount: string;
              currency: string;
            };
          };
        };
      },
      email: "",
      async processSubscriptionOrder() {
        this.loading = true;
        const response = await graphqlFetch(PROCESS_SUBSCRIPTION_ORDER, {
          input: { id: this.subscription.nextScheduledOrder.id },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionOrderProcess.subscriptionOrder.subscription;
          this.feedbackModal = {
            title: "Order Processed",
            content: `<div>Your order has been processed and will be pick, packed & shipped as soon as possible</div><div>You will find the order in your <a href="/account" class="underline">Order History</a> page</div>`,
            link: "/account",
            linkText: "Order History",
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Processing Failed",
            content: `<div>There was an error processing this order. <br/> Please contact support</div>`,
            open: true,
          };
        }

        this.loading = false;
      },
      async setSchedule(event: Event) {
        this.loading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        // console.log(formData.get("date"))
        const response = await graphqlFetch(SET_SUBSCRIPTION_SCHEDULE, {
          input: {
            id: this.subscription.id``,
            nextDeliveryAt: formData.get("date"),
            subscriptionAnchorId: this.subscription.subscriptionAnchor.id,
            subscriptionPlanId: this.subscription.subscriptionPlan.id,
          },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionSetSchedule.subscription;
          this.feedbackModal = {
            title: "Order Rescheduled",
            content: `<div>Your order has been rescheduled to ${formData.get("date")}</div>`,
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Rescheduling Failed",
            content: `<div>There was an error rescheduling this order. <br/> Please contact support</div>`,
            open: true,
          };
        }
        this.loading = false;
      },
      async changeFrequency(event: Event) {
        this.loading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const { selectedPlan } = JSON.parse(formData.get("deliveryFrequency"));
        if (this.subscription.subscriptionAnchor.id != selectedPlan.anchorId) {
          const response = await graphqlFetch(SET_SUBSCRIPTION_SCHEDULE, {
            input: {
              id: this.subscription.id,
              nextDeliveryAt: this.subscription.nextScheduledOrder.expectedDeliveryAt,
              subscriptionAnchorId: selectedPlan.anchorId,
              subscriptionPlanId: selectedPlan.planId,
            },
          });
          try {
          const { data } = response;
          this.subscription = data.subscriptionSetSchedule.subscription;
          this.feedbackModal = {
            title: "Order Frequency Updated",
            content: `<div>Your delivery frequency has been changed to ${selectedPlan.description}.`,
            open: true,
          };
          }
          catch {
            this.feedbackModal = {
              title: "Delivery Frequency Update Failed",
              content: `<div>There was an error editing the delivery frequency with this subscription. <br/> Please contact support</div>`,
              open: true,
            };
          }
        }
        this.loading = false;
      },
      async skipSubscriptionOrder() {
        this.loading = true;
        const response = await graphqlFetch(SKIP_SUBSCRIPTION_ORDER, {
          input: { id: this.subscription.nextScheduledOrder.id },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionOrderSkip.subscriptionOrder.subscription;
          this.feedbackModal = {
            title: "Order Skipped",
            content: `<div>Your next order has been skipped</div>`,
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Skipping Failed",
            content: `<div>Your order has not been skipped <br/> Please contact support</div>`,
            open: true,
          };
        }
        this.loading = false;
      },
      async sendPaymentMethodUpdateEmail() {
        const response = await graphqlFetch(SEND_PAYMENT_METHOD_UPDATE_EMAIL, {
          input: { id: this.subscription.paymentMethod.id },
        });
        const { data } = response;
        if (data.paymentMethodSendUpdateEmail.userErrors.length > 0) {
          console.error("error");
        } else {
          this.paymentMethodUpdateEmailSent = true;
        }
      },
      async allVariantAddOne(): Promise<void> {
        // Define the structure of a line item
        interface LineItem {
          productVariantId: string;
          quantity: number;
        }

        // Ensure subscription exists before proceeding
        if (!this.subscription?.lines) return;

        // Map subscription lines to the required format
        const lines: LineItem[] = this.subscription.lines.map(
          (lineItem: { productVariant: { externalId: any }; quantity: number }) => ({
            productVariantId: `gid://external/ProductVariant/${lineItem.productVariant.externalId}`,
            quantity: lineItem.quantity + 1,
          }),
        );

        // Define the expected response type
        interface SubscriptionUpdateResponse {
          data: {
            subscriptionUpdate: {
              subscription: typeof this.subscription;
            };
          };
        }

        // Perform the GraphQL request
        const response: SubscriptionUpdateResponse = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            lines,
          },
        });

        // Safely update the subscription if response exists
        if (response?.data?.subscriptionUpdate?.subscription) {
          this.subscription = response.data.subscriptionUpdate.subscription;
        }
      },
      async swapFlavour(targetId: string, replacementId: string): Promise<void> {
        this.loading = true;
        // Define the structure of a line item
        interface LineItem {
          productVariantId: string;
          quantity: number;
        }

        // Ensure subscription exists before proceeding
        if (!this.subscription?.lines) return;

        // Map subscription lines and replace targetId with replacementId
        const lines: LineItem[] = this.subscription.lines.map(
          (lineItem: { productVariant: { externalId: any }; quantity: number }) => ({
            productVariantId:
              lineItem.productVariant.externalId === targetId
                ? `gid://external/ProductVariant/${replacementId}`
                : `gid://external/ProductVariant/${lineItem.productVariant.externalId}`,
            quantity: lineItem.quantity,
          }),
        );

        const mergeDuplicateProducts = (lines: LineItem[]): LineItem[] => {
          const productMap: Record<string, LineItem> = {};

          lines.forEach((item) => {
            if (productMap[item.productVariantId]) {
              productMap[item.productVariantId].quantity += item.quantity;
            } else {
              productMap[item.productVariantId] = { ...item };
            }
          });

          return Object.values(productMap);
        };

        const mergedProducts = mergeDuplicateProducts(lines);

        // Define the expected response type
        interface SubscriptionUpdateResponse {
          data: {
            subscriptionUpdate: {
              subscription: typeof this.subscription;
            };
          };
        }

        // Perform the GraphQL request
        const response: SubscriptionUpdateResponse = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            lines: mergedProducts,
          },
        });

        // Safely update the subscription if response exists
        if (response?.data?.subscriptionUpdate?.subscription) {
          this.subscription = response.data.subscriptionUpdate.subscription;
        }
        this.loading = false;
      },
      async updateAddress(event: Event) {
        this.updateAddressLoading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);

        const { data } = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            deliveryMethod: {
              shipping: {
                address: {
                  city: formData.get("city"),
                  country: {
                    code: formData.get("country"),
                  },
                  firstName: formData.get("firstName"),
                  lastName: formData.get("lastName"),
                  postcode: formData.get("postcode"),
                  province: {
                    code: formData.get("province"),
                  },
                  street1: formData.get("street1"),
                  street2: formData.get("street2"),
                  phone: formData.get("phone"),
                },
              },
            },
          },
        });

        this.subscription = data.subscriptionUpdate.subscription;
        this.openAddressUpdateModal = false;
        this.updateAddressLoading = false;
      },
      async cancelSubscription(event: Event) {
        this.loading = true;
        const { data } = await graphqlFetch(CANCEL_SUBSCRIPTION, {
          input: {
            id: this.subscription.id,
          },
        });

        this.subscription = data.subscriptionCancel.subscription;
        this.loading = false
        this.feedbackModal = {
          title: "Subscription Cancelled",
          content: `<div>The subscription has been cancelled. <br/> Please contact support if this was a mistake.</div>`,
          open: true,
        };
      },
      async init() {
        const { data } = await graphqlFetch(SUBSCRIPTION_QUERY, { subscriptionId });
        this.subscription = data.subscription;
        this.address = data.subscription.deliveryMethod.address;
        this.loading = false;
        this.updateAddressLoading = false;
      },
    };
  });

  Alpine.data("submarineGetSubscriptions", (customerId: String) => {
    return {
      loading: true,
      subscriptions: [] as {
        id: string;
        order: { name: string };
        nextScheduledOrder: {
          expectedDeliveryAt: Date;
          status: string;
          paymentIntent: {
            amount: {
              amount: string;
              currency: string;
            };
          };
        };
        daysToNextScheduledOrder: number;
        subscriptionAnchor: {
          frequency: string;
        };
      }[],
      daysUntilDate(dateString: string): number {
        formatter.format(new Date(dateString))
        const today = new Date(); // Current date
        today.setHours(0, 0, 0, 0); // Normalize to midnight to avoid time differences
      
        const targetDate = new Date(dateString);
        targetDate.setHours(0, 0, 0, 0); // Normalize to midnight
      
        const diffTime = targetDate.getTime() - today.getTime(); // Difference in milliseconds
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)); // Convert ms to days
      
        return diffDays;
      },
      async init() {
        const { data } = await graphqlFetch(
          `
            #graphql
            query($customerIds: [SharedGlobalID!]){
                searchSubscriptions(customerIds: $customerIds) {
                    subscriptions {
                        nodes {
                            id
                            order {
                                name
                            }
                            nextScheduledOrder {
                                expectedDeliveryAt
                                status
                                paymentIntent {
                                    amount {
                                        amount
                                        currency
                                    }
                                }
                            }
                            subscriptionAnchor {
                                frequency: name
                            }
                            status
                        }
                    }
                }
            }`,
          { customerIds: [customerId] },
        );
        // this.subscriptions = data.searchSubscriptions.subscriptions.nodes.map((sub) => ({
        //   ...sub,
        //   daysToNextScheduledOrder: daysUntilDate(
        //     formatter.format(new Date(sub.nextScheduledOrder.expectedDeliveryAt)),
        //   ),
        //   nextScheduledOrder: {
        //     ...sub.nextScheduledOrder,
        //     expectedDeliveryAt: formatter.format(new Date(sub.nextScheduledOrder.expectedDeliveryAt)),
        //   },
        // }));

        this.subscriptions = data.searchSubscriptions.subscriptions.nodes

        this.loading = false;
      },
    };
  });

  Alpine.data("submarine", () => {
    return {
      loading: true,
      subscriptions: [] as { id: string; name: string; status: string }[],
      async init() {
        const { data } = await graphqlFetch(
          /* GraphQL */ `
            query {
              subscriptionPlans {
                nodes {
                  id
                  name
                  status
                }
              }
            }
          `,
          {},
        );

        this.subscriptions = data.subscriptionPlans.nodes;
        this.loading = false;
      },
    };
  });
}

/**
 <div x-data="submarine()" class="my-12 bg-blue-100 p-4">
 <p>Submarine:</p>

 <template x-if="loading">
 <p>Loading...</p>
 </template>

 <ul>
 <template x-for="subscription in subscriptions">
 <li>
 <p x-text="subscription.id"></p>
 <p x-text="subscription.name"></p>
 <p x-text="subscription.status"></p>
 </li>
 </template>
 </ul>
 </div>
 */

export const SUBSCRIPTION_FRAGMENT = `#graphql
 fragment SubscriptionDetails on Subscription {
 status
  availableSubscriptionPlans {
      id
      status
      anchors {
        id
        position
      }
      frequency {
        description
      }
    }
  subscriptionPlan {
    id
  }
  subscriptionAnchor {
    id
  }
  id
  processedSubscriptionOrdersCount
  order {
    name
  }
  lines {
    quantity
    productVariant {
    externalId
      title
      imageUrl
    }
    product {
        productVariants {
          imageUrl
          externalId
          title
        }
        title
      }
  }
  lastProcessedOrder {
    processedAt
    expectedBillingAt
  }
  nextBillingAt
  paymentMethod {
  id
    activePaymentInstrument {
      paymentSource {
        ... on Card {
          last4
          brand
        }
      }
    }
  }
  deliveryMethod {
    ... on SubscriptionDeliveryMethodShipping {
      address {
        city
        country {
          name
          code
        }
        firstName
        lastName
        phone
        postcode
        province {
          code
        }
        street1
        street2
      }
    }
  }
  nextScheduledOrder {

    nextSubscriptionOrder {
        expectedDeliveryAt
      }
    id
    lastPaymentMethodUpdateEvent {
      action
      createdAt
      description
    }
    expectedDeliveryAt
    paymentIntent {
      amount {
        amount
        currency
      }
    }
    financials {
      totalPrice {
        amount
      }
      subtotal {
        amount
      }
      discounts {
        total {
          amount
        }
      }
    }
  }
}`;

const SUBSCRIPTION_QUERY = `#graphql
${SUBSCRIPTION_FRAGMENT}
query SearchSubscriptions($subscriptionId: GlobalID!) {
  subscription(id: $subscriptionId) {
    ...SubscriptionDetails
  }
}`;

const SUBSCRIPTION_UPDATE_MUTATION_OBJECT = `#graphql
${SUBSCRIPTION_FRAGMENT}
mutation SubscriptionMutation($input: SubscriptionUpdateInput!) {
  subscriptionUpdate(input: $input) {
    subscription {
      ...SubscriptionDetails
    }
    userErrors {
      field
      message
    }
  }
}`;

const CANCEL_SUBSCRIPTION = `#graphql
${SUBSCRIPTION_FRAGMENT}
  mutation subscriptionCancel($input: SubscriptionCancelInput!) {
    subscriptionCancel(input: $input) {
      subscription {
        ...SubscriptionDetails
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const PROCESS_SUBSCRIPTION_ORDER = `#graphql
${SUBSCRIPTION_FRAGMENT}
  mutation SubscriptionOrderProcess($input: SubscriptionOrderProcessInput!) {
    subscriptionOrderProcess(input: $input) {
      subscriptionOrder {
        id
        paymentStatus
        status
        subscription {
          ...SubscriptionDetails
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;
export const SKIP_SUBSCRIPTION_ORDER = `#graphql
 ${SUBSCRIPTION_FRAGMENT}
  mutation subscriptionOrderSkip($input: SubscriptionOrderSkipInput!) {
    subscriptionOrderSkip(input: $input) {
      subscriptionOrder {
        id
        status
        subscription {
          ...SubscriptionDetails
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const PAYMENT_METHOD_DETAILS = `
  activePaymentInstrument {
    description
    paymentSource {
      ... on Card {
        brand
        expiry {
          month
          year
        }
        last4
      }
    }
  }
  externalId
  id
  status
`;

export const GET_PAYMENT_METHOD = `#graphql
  query paymentMethod($id: GlobalID!) {
    paymentMethod(id: $id) {
      ${PAYMENT_METHOD_DETAILS}
    }
  }
`;

export const SEND_PAYMENT_METHOD_UPDATE_EMAIL = `#graphql
  mutation paymentMethodSendUpdateEmail($input: PaymentMethodSendUpdateEmailInput!) {
    paymentMethodSendUpdateEmail(input: $input) {
      userErrors {
        field
        message
      }
    }
  }
`;

export const SET_SUBSCRIPTION_SCHEDULE = `#graphql
 ${SUBSCRIPTION_FRAGMENT}
 mutation SubscriptionSetSchedule($input: SubscriptionSetScheduleInput!) {
  subscriptionSetSchedule(input: $input) {
    subscription {
      ...SubscriptionDetails
    }
    upcomingDeliverySlots {
      deliverAt
    }
    userErrors {
      field
      message
    }
  }
}
`;
